// wrapper component for svg icons
// this allows an svg to be recoloured using the current text colour
// (similar to font icons)
// Use case: themes with light and dark modes
// IMPORTANT! to be recoloured, the svg must not have fill attributes
// if the icon has previously been loaded, fetch will return 304 meaning the cached version will be used

/*
    Background info: an <img> tab with an SVG file as src cannot be recoloured using CSS.
    This component allows you to recolour the SVG using the current text colour.
    This method fetches the svg as raw data and parses it into an SVG element
    Alternatively we would have to refactor the SVG into a React component
    Or else use mask or filter CSS to recolour the SVG
*/
import { SvgIconWrapper } from './SvgIcon.styles';
import { useEffect, useState } from 'react';
import { sanitizeHtml } from 'utils/sanitize';
import { useSessionContext } from 'utils/context/session-provider';

export type SvgIconProps = {
  src: string;
  alt?: string;
  height?: number;
  width?: number;
  className?: string;
};

export const SvgIcon = ({
  src,
  alt,
  height = 24,
  width = 24,
  className,
}: SvgIconProps) => {
  const { svgIcons, setSvgIcons } = useSessionContext();
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    const fetchSvg = async () => {
      if (!svgIcons[src]) {
        // don't refetch while awaiting a fetch
        setSvgIcons({ ...svgIcons, [src]: -1 });

        try {
          const response = await fetch(src);
          const data = await response.text();
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(data, 'image/svg+xml');

          const svgElement = svgDoc.querySelector('svg');

          setSvgIcons((prevSvgIcons) => ({
            ...prevSvgIcons,
            [src]: svgElement?.outerHTML,
          }));
        } catch (error) {
          // if there is a fetch or data error then we do nothing
        }
      }
    };

    fetchSvg();
  }, [src]);

  useEffect(() => {
    const savedSvgIcon = svgIcons[src];

    if (typeof savedSvgIcon === 'string') {
      setSvgContent(savedSvgIcon);
    }
  }, [svgIcons, src]);

  return (
    <SvgIconWrapper
      height={height}
      width={width}
      role='graphics-symbol'
      aria-labelledby={alt}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(svgContent) }}
      className={className}
    ></SvgIconWrapper>
  );
};
